import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { ChevronDown } from 'lucide-react';

const PROJECT_TYPES = [
  { value: 'tresorerie', label: 'Trésorerie' },
  { value: 'vehicule_neuf', label: 'Véhicule neuf' },
  { value: 'vehicule_occasion', label: "Véhicule d'occasion" },
  { value: 'rachat_credits', label: 'Rachat de crédits' },
  { value: 'travaux', label: 'Travaux' },
  { value: 'equipement', label: 'Équipement maison' },
  { value: 'voyage', label: 'Voyage' },
  { value: 'autre', label: 'Autre projet' },
];

const SimulationStep1 = () => {
  const navigate = useNavigate();
  const [projectType, setProjectType] = useState('');
  const [amount, setAmount] = useState(10000);
  const [duration, setDuration] = useState(48);

  const handleContinue = () => {
    if (!projectType) {
      alert('Veuillez sélectionner un type de projet');
      return;
    }
    // Store in sessionStorage for next step
    sessionStorage.setItem('simulation_step1', JSON.stringify({
      projectType,
      projectTypeLabel: PROJECT_TYPES.find(p => p.value === projectType)?.label,
      amount,
      duration
    }));
    navigate('/simulation/step2');
  };

  // Calculate monthly payment estimate
  const taeg = 5.9;
  const monthlyRate = taeg / 100 / 12;
  const monthlyPayment = (amount * monthlyRate * Math.pow(1 + monthlyRate, duration)) / (Math.pow(1 + monthlyRate, duration) - 1);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-black py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <img 
            src="https://customer-assets.emergentagent.com/job_younited-clone-1/artifacts/y6hpz6h2_IMG_0359.jpeg"
            alt="Younited"
            className="h-10 object-contain cursor-pointer"
            onClick={() => navigate('/')}
          />
          <Button 
            variant="outline" 
            className="text-white border-white hover:bg-white hover:text-black rounded-full px-6"
            onClick={() => navigate('/login')}
          >
            Espace Client
          </Button>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="bg-white border-b py-6">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-center space-x-8">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-sm">1</div>
              <span className="text-purple-600 font-medium">Votre projet</span>
            </div>
            <div className="w-16 h-0.5 bg-gray-300"></div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-gray-300 text-gray-500 flex items-center justify-center font-bold text-sm">2</div>
              <span className="text-gray-400">Vos coordonnées</span>
            </div>
            <div className="w-16 h-0.5 bg-gray-300"></div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-gray-300 text-gray-500 flex items-center justify-center font-bold text-sm">3</div>
              <span className="text-gray-400">Récapitulatif</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 py-12">
        <Card className="p-8 shadow-lg">
          <h1 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Votre projet de crédit
          </h1>

          {/* Project Type */}
          <div className="mb-8">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Type de projet
            </label>
            <div className="relative">
              <select
                value={projectType}
                onChange={(e) => setProjectType(e.target.value)}
                className="w-full h-14 px-4 pr-12 rounded-xl border-2 border-gray-200 appearance-none text-gray-700 font-medium text-lg focus:border-purple-500 focus:ring-purple-500 cursor-pointer bg-white"
              >
                <option value="">Sélectionnez votre projet</option>
                {PROJECT_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 h-6 w-6 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Amount Slider */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-semibold text-gray-700">
                Montant souhaité
              </label>
              <span className="text-2xl font-bold text-purple-600">{amount.toLocaleString()} €</span>
            </div>
            <input
              type="range"
              min="1000"
              max="60000"
              step="500"
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              className="w-full h-3 bg-gray-200 rounded-full appearance-none cursor-pointer accent-black"
              style={{ accentColor: '#000' }}
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>1 000 €</span>
              <span>60 000 €</span>
            </div>
          </div>

          {/* Duration Slider */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-semibold text-gray-700">
                Durée de remboursement
              </label>
              <span className="text-2xl font-bold text-purple-600">{duration} mois</span>
            </div>
            <input
              type="range"
              min="12"
              max="84"
              step="6"
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              className="w-full h-3 bg-gray-200 rounded-full appearance-none cursor-pointer"
              style={{ accentColor: '#000' }}
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>12 mois</span>
              <span>84 mois</span>
            </div>
          </div>

          {/* Estimated Monthly Payment */}
          <div className="bg-purple-50 rounded-xl p-6 mb-8 text-center">
            <p className="text-gray-600 mb-2">Mensualité estimée</p>
            <p className="text-4xl font-bold text-purple-600">{monthlyPayment.toFixed(2)} € <span className="text-lg font-normal text-gray-500">/mois</span></p>
            <p className="text-xs text-gray-500 mt-2">TAEG fixe: {taeg}%*</p>
          </div>

          {/* Continue Button */}
          <Button
            onClick={handleContinue}
            className="w-full h-14 text-white font-bold text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
            style={{ backgroundColor: '#8B5CF6' }}
          >
            Continuer
          </Button>

          <p className="text-xs text-gray-500 text-center mt-4">
            *Taux annuel effectif global. Un crédit vous engage et doit être remboursé.
          </p>
        </Card>
      </div>
    </div>
  );
};

export default SimulationStep1;
